import React, { useEffect, useMemo, useRef } from "react";
import { RiBarChart2Line } from "react-icons/ri";
import { Chart } from "chart.js/auto";
import { currencyDisplay } from "../helpers/commonHelper";

const HourlySalesGraph = ({ hourlyData,title }) => {
  // Group and sum the amounts for each hour
  const summedData = useMemo(() => {
    return hourlyData.reduce((acc, curr) => {
      const existing = acc.find((item) => item.hour === curr.hour);
      if (existing) {
        existing.amount += curr.amount;
      } else {
        acc.push({
          hour: curr.hour,
          amount: curr.amount,
        });
      }
      return acc;
    }, []);
  }, [hourlyData]);

  const clientData = JSON.parse(
    sessionStorage.getItem("clientSettings") || "null",
  );

  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  // Calculate dynamic graph height
  const calculateGraphHeight = () => {
    const baseHeight = 200;
    const incrementHeight = 20;
    const maxHeight = 500;
    const minHeight = 200;

    const dynamicHeight = baseHeight + hourlyData.length * incrementHeight;
    return Math.min(maxHeight, Math.max(minHeight, dynamicHeight));
  };

  useEffect(() => {
    if (chartRef.current) {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }

      const ctx = chartRef.current.getContext("2d");

      const gradient = ctx.createLinearGradient(0, 0, 300, 0);
      gradient.addColorStop(0, "#6366F1");
      gradient.addColorStop(1, "#818CF8");

      chartInstance.current = new Chart(ctx, {
        type: "bar",
        data: {
          labels: summedData.map((item) => item.hour),
          datasets: [
            {
              data: summedData.map((item) => currencyDisplay(item.amount)), // Only number here
              backgroundColor: "#FFFFFF",
              borderRadius: {
                topRight: 4,
                bottomRight: 4,
              },
              borderSkipped: "left",
              barPercentage: 0.8,
              categoryPercentage: 0.9,
            },
          ],
        },
        options: {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          animation: {
            duration: 1000,
            delay: (context) => context.dataIndex * 100,
          },
          scales: {
            x: {
              beginAtZero: true,
              ticks: {
                color: "#FFFFFF",
                font: {
                  family: "QSemiBold, sans-serif",
                  weight: "500",
                  size: 12,
                },
              },
              grid: {
                color: "rgba(255,255,255,0.15)",
                drawTicks: false,
              },
              title: {
                display: true,
                text: "Amount",
                color: "#FFFFFF",
              },
            },

            y: {
              ticks: {
                color: "#FFFFFF",
                font: {
                  family: "QSemiBold, sans-serif",
                  weight: "500",
                  size: 12,
                },
              },
              grid: { display: false },
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: true,
              callbacks: {
                label: (context) =>
                  `${clientData?.mainCurrencyName || ""} ${context.parsed.x}`, // Currency + value in tooltip
                title: (context) => `${context[0].label}`,
              },
              displayColors: false,
              backgroundColor: "#0F172A",
              titleColor: "#FFFFFF",
              bodyColor: "#FFFFFF",

              bodyFont: {
                family: "QSemiBold, sans-serif",
                weight: "500",
                size: 12,
              },
              padding: 8,
              cornerRadius: 4,
              yAlign: "center",
            },
            datalabels: {
              display: false,
            },
          },
        },
        plugins: [
          {
            id: "showValuesInsideBars",
            afterDatasetsDraw(chart) {
              const { ctx } = chart;
              ctx.save();
              ctx.font = "12px QSemiBold";
              ctx.textAlign = "left";
              ctx.textBaseline = "middle";

              chart.data.datasets.forEach((dataset, i) => {
                const meta = chart.getDatasetMeta(i);
                meta.data.forEach((bar, index) => {
                  const value = dataset.data[index];
                  const text = `${clientData?.mainCurrencyName || ""} ${value}`; // Currency + amount inside bar
                  const textWidth = ctx.measureText(text).width;
                  const barX = bar.x;
                  const barY = bar.y;
                  const barStartX = bar.base;
                  const barWidth = Math.abs(barX - barStartX);
                  const isWide = barWidth > textWidth + 12;

                  if (barWidth > textWidth + 12) {
                    ctx.fillStyle = "#111827"; // inside bar
                    ctx.fillText(text, barX - textWidth - 8, barY);
                  } else {
                    ctx.fillStyle = "#FFFFFF"; // outside bar
                    ctx.fillText(text, barX + 6, barY);
                  }
                });
              });

              ctx.restore();
            },
          },
        ],
      });
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [summedData]);

  return (
    <div
      className="rounded-xl shadow-sm border border-gray-200 overflow-hidden
bg-gradient-to-br from-indigo-500 via-indigo-600 to-indigo-700"
    >
      {/* Header */}
      <div className="p-5 border-b border-indigo-500 flex items-center">
        <RiBarChart2Line className="text-white text-xl mr-2" />
        <h3 className="font-QSemiBold text-sm text-white">
          {title}
        </h3>
      </div>

      {/* Chart */}
      <div className="p-5">
        <div
          className="h-full w-full relative"
          style={{ height: `${calculateGraphHeight()}px` }}
        >
          <canvas ref={chartRef} />
        </div>

        {/* Hour-wise Detail List */}
        <div className="bg-white rounded-2xl h-[300px] overflow-y-auto shadow-sm overflow-hidden mt-6 border border-gray-100">
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="flex justify-between text-sm font-QBold text-gray-900">
              <span>Hour</span>
              <span>Amount</span>
            </div>
          </div>

          <ul className="divide-y divide-gray-200">
            {summedData &&
              summedData.map((item, index) => (
                <li
                  key={index}
                  className="px-4 py-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <div
                        style={{ backgroundColor: item.color || "#6366F1" }}
                        className="w-3 h-3 rounded-full mr-3"
                      ></div>
                      <span className="text-xs font-QSemiBold text-gray-900 tracking-wide">
                        {item.hour}
                      </span>
                    </div>
                    {item.amount && (
                      <span className="text-xs font-QSemiBold text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {clientData?.mainCurrencyName}{" "}
                        {currencyDisplay(item.amount)}
                      </span>
                    )}
                  </div>
                </li>
              ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default HourlySalesGraph;
